<template>
  <transition-group name="toast-slide" tag="div" class="app-toast-container">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      :class="['app-toast', `toast-${toast.type}`]"
    >
      <div class="toast-icon-wrapper">
        <svg v-if="toast.type === 'success'" class="toast-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else-if="toast.type === 'error'" class="toast-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else class="toast-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="toast-content">
        <div v-if="toast.title" class="toast-title">{{ toast.title }}</div>
        <div class="toast-message">{{ toast.message }}</div>
      </div>
      <button class="toast-close" @click="removeToast(toast.id)">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </transition-group>
</template>

<script>
import Vue from 'vue';

// Create a simple event bus for toast notifications
export const toastBus = new Vue();

export default {
  name: 'AppToast',
  data() {
    return {
      toasts: [],
      toastId: 0
    };
  },
  created() {
    toastBus.$on('show-toast', this.showToast);
  },
  beforeDestroy() {
    toastBus.$off('show-toast', this.showToast);
  },
  methods: {
    showToast({ type = 'info', message, title = null, duration = 5000 }) {
      const id = ++this.toastId;
      const toast = { id, type, message, title };

      this.toasts.push(toast);

      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(id);
        }, duration);
      }
    },
    removeToast(id) {
      const index = this.toasts.findIndex(t => t.id === id);
      if (index > -1) {
        this.toasts.splice(index, 1);
      }
    }
  }
};
</script>

<style scoped>
.app-toast-container {
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 40rem;
}

.app-toast {
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
  padding: 1.4rem 1.6rem;
  background: #ffffff;
  border-radius: 0.8rem;
  box-shadow: 0 4px 20px rgba(43, 43, 65, 0.15);
  border-left: 0.5rem solid;
  min-width: 30rem;
}

/* Success toast */
.toast-success {
  border-left-color: #0acab1;
}

.toast-success .toast-icon-wrapper {
  background-color: #e2fefa;
  color: #0acab1;
}

.toast-success .toast-title {
  color: #0acab1;
}

/* Error toast */
.toast-error {
  border-left-color: #F14668;
}

.toast-error .toast-icon-wrapper {
  background-color: #ffe8ed;
  color: #F14668;
}

.toast-error .toast-title {
  color: #F14668;
}

/* Info toast (default) */
.toast-info {
  border-left-color: #1e33f2;
}

.toast-info .toast-icon-wrapper {
  background-color: #eff1fd;
  color: #1e33f2;
}

.toast-info .toast-title {
  color: #1e33f2;
}

/* Warning toast */
.toast-warning {
  border-left-color: #eea507;
}

.toast-warning .toast-icon-wrapper {
  background-color: #fef2e2;
  color: #eea507;
}

.toast-warning .toast-title {
  color: #eea507;
}

.toast-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  flex-shrink: 0;
}

.toast-icon {
  width: 2rem;
  height: 2rem;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-family: 'Barlow', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.toast-message {
  font-family: 'Barlow', sans-serif;
  font-size: 1.3rem;
  color: #606076;
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #606076;
  padding: 0;
  border-radius: 0.4rem;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toast-close:hover {
  background-color: #f2f2f2;
  color: #2b2b41;
}

.toast-close svg {
  width: 1.6rem;
  height: 1.6rem;
}

/* Transition animations */
.toast-slide-enter-active {
  animation: toast-in 0.3s ease-out;
}

.toast-slide-leave-active {
  animation: toast-out 0.3s ease-in;
}

@keyframes toast-in {
  0% {
    opacity: 0;
    transform: translateX(100%);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes toast-out {
  0% {
    opacity: 1;
    transform: translateX(0);
  }
  100% {
    opacity: 0;
    transform: translateX(100%);
  }
}

/* Responsive adjustments */
@media screen and (max-width: 560px) {
  .app-toast-container {
    top: 1rem;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }

  .app-toast {
    min-width: auto;
  }
}
</style>
